import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";

const SERVICES_PREVIEW = [
  "Tax Advisory & Planning",
  "Statutory & Bank Audits",
  "Internal Audit & Risk",
  "Due Diligence",
  "Accounting & Bookkeeping",
  "Startup & Business Advisory",
];

export default function Home() {
  const [shorts, setShorts] = useState([]);
  const [shortsLoading, setShortsLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get("/youtube/recent", { params: { limit: 5 } })
      .then(({ data }) => { if (!cancel) setShorts(data.items || []); })
      .catch(() => { /* swallow — section just hides */ })
      .finally(() => { if (!cancel) setShortsLoading(false); });
    return () => { cancel = true; };
  }, []);

  return (
    <>
      {/* HERO */}
      <section data-testid="hero" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 md:pt-20 pb-12 md:pb-20">
          <p className="overline fade-up"><span className="section-mark">Chartered Accountant · Founder · Creator</span></p>

          <h1 className="font-display mt-7 text-[44px] sm:text-6xl md:text-7xl lg:text-[92px] font-light leading-[0.98] tracking-[-0.03em] text-ink fade-up fade-up-2">
            Finance you can <span className="font-display-italic text-terracotta">trust</span>.<br />
            Stories that <span className="font-display-italic">scale</span>.
          </h1>

          <p className="max-w-2xl mt-8 text-[15px] sm:text-lg text-ink-soft leading-[1.7] fade-up fade-up-3">
            I&apos;m CA Nitin Sharma — Chartered Accountant, Partner at Sharma Nitin &amp; Co.,
            and founder of Stealodeal. I help founders, professionals and brands make
            smarter financial decisions, on screen and off it.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 fade-up fade-up-4">
            <Link
              to="/contact"
              data-testid="hero-cta-discovery"
              className="inline-flex items-center justify-center rounded-full bg-ink text-white px-7 py-4 text-[14px] font-medium tracking-wide hover:bg-terracotta transition-colors"
            >
              Book a Discovery Call <ArrowUpRight className="ml-2" size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-collab"
              className="inline-flex items-center justify-center rounded-full border border-stone-300 px-7 py-4 text-[14px] font-medium tracking-wide text-ink hover:border-ink transition-colors"
            >
              Start a Collaboration
            </Link>
          </div>
        </div>

        {/* Dual portrait split */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 md:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
            <PersonaCard
              testid="persona-pro"
              tag="01 — For clients"
              title="The CA Practice"
              copy="Tax, audits, due diligence and advisory delivered with the precision of a partner-led firm."
              cta="Explore Services"
              ctaTo="/services"
              img="/nitin-pro.png"
              fallback="bg-stone-100"
            />
            <PersonaCard
              testid="persona-creator"
              tag="02 — For brands"
              title="The Creator"
              copy="A finance-first audience on YouTube and Instagram — campaigns that earn attention and trust."
              cta="See Collaborations"
              ctaTo="/collaborations"
              img="/nitin-creator.png"
              fallback="bg-[#f4ddd2]"
              accent
            />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-[hsl(var(--line))] bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 md:py-12 grid grid-cols-2 md:grid-cols-4 gap-8 items-start">
          <Stat k="10+ yrs" v="In Tax & Audit practice" />
          <Stat k="2014" v="Founded Stealodeal" />
          <Stat k="130K+" v="Followers across socials" />
          <Stat k="Pan-India" v="Clients & creators served" />
        </div>
      </section>

      {/* DUAL EXPERTISE */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32">
        <p className="overline"><span className="section-mark">Two practices · One mission</span></p>
        <h2 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.03em] text-ink leading-[1.04]">
          Built like a firm.<br />Run like a <span className="font-display-italic">creator</span>.
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <ExpertiseCard
            testid="expertise-firm"
            tag="Sharma Nitin & Co."
            title="Audits, taxation and advisory for businesses that take their books seriously."
            points={[
              "Statutory & bank audits",
              "Internal audit & risk advisory",
              "Direct & indirect taxation",
              "Due diligence & transactions",
            ]}
            href="/services"
            cta="View services"
          />
          <ExpertiseCard
            testid="expertise-stealodeal"
            tag="Stealodeal · since 2014"
            title="Founder’s instinct, built into every consultation. A consumer brand serving a national customer base."
            points={[
              "International business development",
              "Operations & growth playbooks",
              "Customer-centric ecommerce ops",
              "Founder-to-founder advisory",
            ]}
            href="/about"
            cta="My journey"
            tone="dark"
          />
        </div>
      </section>

      {/* RECENT SHORTS — pulled live from YouTube RSS, cached server-side 1h */}
      {(shortsLoading || shorts.length > 0) && (
        <section className="bg-white border-t border-[hsl(var(--line))]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
              <div>
                <p className="overline"><span className="section-mark">Latest on YouTube</span></p>
                <h2 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.03em] text-ink leading-[1.05]">
                  Fresh from the<br /><span className="font-display-italic">channel</span>.
                </h2>
              </div>
              <a
                href="https://www.youtube.com/@ca_nitinsharma"
                target="_blank"
                rel="noreferrer"
                data-testid="home-yt-cta"
                className="inline-flex items-center text-[13px] tracking-wide font-medium text-ink hover:text-terracotta"
              >
                Subscribe on YouTube <ArrowUpRight className="ml-1" size={14} />
              </a>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {shortsLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="aspect-[9/16] rounded-2xl bg-stone-100 animate-pulse" />
                  ))
                : shorts.map((v) => (
                    <a
                      key={v.id}
                      data-testid={`home-short-${v.id}`}
                      href={`https://www.youtube.com/shorts/${v.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="group block card-lift"
                    >
                      <div className="relative aspect-[9/16] overflow-hidden rounded-2xl shorts-frame ring-1 ring-stone-200">
                        <img
                          src={`https://i.ytimg.com/vi/${v.id}/oar2.jpg`}
                          alt={v.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                          onError={(e) => {
                            if (!e.currentTarget.dataset.fallback) {
                              e.currentTarget.dataset.fallback = "1";
                              e.currentTarget.src = `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`;
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <span className="rounded-full bg-white/95 backdrop-blur w-12 h-12 grid place-items-center shadow-lg">
                            <span className="ml-0.5 w-0 h-0 border-y-[7px] border-y-transparent border-l-[11px] border-l-ink" />
                          </span>
                        </div>
                        <div className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[10px] font-semibold tracking-wider uppercase text-ink">
                          <span className="w-1.5 h-1.5 rounded-full bg-terracotta" /> Short
                        </div>
                        {Number.isFinite(v.view_count) && v.view_count > 0 && (
                          <div
                            data-testid={`home-short-views-${v.id}`}
                            className="absolute bottom-3 left-3 right-3 flex items-center gap-1.5 text-white text-[12px] font-semibold tracking-wide"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            <span className="tabular">{formatViews(v.view_count)} views</span>
                          </div>
                        )}
                      </div>
                      {v.title && (
                        <p className="mt-3 text-[13px] font-medium text-ink line-clamp-2 group-hover:text-terracotta transition-colors">
                          {v.title}
                        </p>
                      )}
                    </a>
                  ))}
            </div>
          </div>
        </section>
      )}

      {/* SERVICES PREVIEW */}
      <section className="border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <p className="overline"><span className="section-mark">What I do</span></p>
              <h2 className="font-display mt-6 text-4xl sm:text-5xl font-light tracking-[-0.03em] text-ink leading-[1.05]">
                Quietly excellent.<br />Surprisingly <span className="font-display-italic">accessible</span>.
              </h2>
              <p className="mt-7 text-ink-soft text-[15px] md:text-lg max-w-md leading-[1.7]">
                Whether you&apos;re an individual figuring out tax season, a founder
                preparing for diligence, or a brand looking for a credible voice —
                start with a 30–45 min conversation.
              </p>
              <Link
                to="/services"
                data-testid="home-services-cta"
                className="mt-9 inline-flex items-center rounded-full border border-stone-300 px-6 py-3 text-[13px] font-medium tracking-wide hover:border-ink"
              >
                Explore all services <ArrowUpRight className="ml-2" size={14} />
              </Link>
            </div>
            <ul className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2">
              {SERVICES_PREVIEW.map((s, i) => (
                <li
                  key={s}
                  data-testid={`home-service-${i}`}
                  className="group flex items-center justify-between border-b border-[hsl(var(--line))] py-5"
                >
                  <span className="font-display text-lg md:text-xl font-light tracking-tight text-ink">{s}</span>
                  <ArrowUpRight size={16} className="text-stone-400 group-hover:text-terracotta transition-colors" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <p className="font-display tabular text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.03em] text-ink">{k}</p>
      <p className="mt-2 text-[13px] text-ink-soft tracking-wide">{v}</p>
    </div>
  );
}

function formatViews(n) {
  if (!Number.isFinite(n) || n < 0) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const v = n / 1000;
    return (v >= 100 ? Math.round(v) : v.toFixed(v >= 10 ? 0 : 1)) + "K";
  }
  if (n < 1_000_000_000) {
    const v = n / 1_000_000;
    return (v >= 100 ? Math.round(v) : v.toFixed(v >= 10 ? 0 : 1)) + "M";
  }
  return (n / 1_000_000_000).toFixed(1) + "B";
}

function PersonaCard({ testid, tag, title, copy, cta, ctaTo, img, fallback, accent }) {
  return (
    <Link
      to={ctaTo}
      data-testid={testid}
      className={`group relative overflow-hidden rounded-2xl border border-[hsl(var(--line))] card-lift ${
        accent ? "bg-[#f4ddd2]" : "bg-white"
      }`}
    >
      <div className={`relative aspect-[4/5] sm:aspect-[4/5] ${fallback}`}>
        <img
          src={img}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-black/65 via-black/20 to-transparent text-white">
        <p className="text-[10px] tracking-[0.22em] uppercase font-semibold opacity-85">{tag}</p>
        <h3 className="font-display mt-3 text-2xl md:text-3xl font-light tracking-[-0.02em]">{title}</h3>
        <p className="mt-2 text-[13px] opacity-90 max-w-sm leading-relaxed">{copy}</p>
        <span className="mt-5 inline-flex items-center text-[13px] font-medium underline-offset-4 group-hover:underline">
          {cta} <ArrowUpRight className="ml-1" size={14} />
        </span>
      </div>
    </Link>
  );
}

function ExpertiseCard({ testid, tag, title, points, href, cta, tone = "light" }) {
  const dark = tone === "dark";
  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl p-8 md:p-12 border ${
        dark ? "bg-ink text-white border-ink" : "bg-white text-ink border-[hsl(var(--line))]"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold opacity-80">
        {dark ? <Sparkles size={13} /> : <BadgeCheck size={13} />}
        {tag}
      </div>
      <h3 className={`font-display mt-7 text-2xl md:text-3xl font-light leading-[1.2] tracking-[-0.02em] ${dark ? "text-white" : "text-ink"}`}>
        {title}
      </h3>
      <ul className={`mt-8 space-y-2.5 ${dark ? "text-white/80" : "text-ink-soft"} text-[15px]`}>
        {points.map((p) => (
          <li key={p} className="flex items-start gap-3">
            <span className={`mt-2 inline-block w-1.5 h-1.5 rounded-full bg-terracotta`} />
            {p}
          </li>
        ))}
      </ul>
      <Link
        to={href}
        className={`mt-9 inline-flex items-center text-[13px] font-medium tracking-wide ${
          dark ? "text-white hover:text-terracotta" : "text-ink hover:text-terracotta"
        }`}
      >
        {cta} <ArrowUpRight className="ml-1" size={14} />
      </Link>
    </div>
  );
}
